import Panel, { PanelTitle } from 'components/layout/Panel'
import { get, toLower } from 'lodash'
import { Container, Spacer, Text } from 'roadie-ui'
import { CaseStatusKeyMap, CaseType } from 'util/salesforce'
import { LocalizedRichText, LocalizedText, useLocalization } from 'localization'

const formatLastUpdated = (lastUpdated: string | null | undefined, locale: string) => {
  if (!lastUpdated) return ''

  const date = new Date(lastUpdated)
  if (Number.isNaN(date.getTime())) return ''

  return date.toLocaleDateString(locale)
}

const CaseStatus: React.FC<{ summary: any; type: CaseType }> = ({ summary, type }) => {
  const { locale } = useLocalization()
  const status = toLower(get(summary, 'status', ''))
  const statusKey = CaseStatusKeyMap[status]
  const lastUpdatedFormatted = formatLastUpdated(get(summary, 'last_updated'), locale)

  return (
    <>
      <Panel noPadding>
        <Container.Base
          $backgroundColor="secondary-lighter"
          $padding="2.4rem"
          $borderColor="neutral-base"
          $borderStyle="solid"
          $borderWidth="0 0 2px 0"
        >
          <Text.Body2 $color="neutral-darker">
            <LocalizedText translationKey="dweb_dashboard_salesforce_case_status_status" />
          </Text.Body2>
          <Spacer $height="0.8rem" />
          <Container.Grid
            $gridTemplateColumns="auto auto"
            $gridGap="0.8rem"
            $justifyContent="center"
            $alignItems="center"
          >
            <Text.H5 $fontWeight="bold" $color="neutral-black" $whiteSpace="nowrap">
              {statusKey ? <LocalizedText translationKey={statusKey} /> : '-'}
            </Text.H5>
          </Container.Grid>
          <Spacer $height="0.8rem" />
          <Text.Caption $color="neutral-darker">
            <LocalizedRichText
              translationKey="dweb_dashboard_return_rate_last_updated_value1"
              params={{ value1: lastUpdatedFormatted }}
            />
          </Text.Caption>
        </Container.Base>
        <Container.Base $padding="2.4rem" $textAlign="left">
          <Text.Body2 $color="neutral-darker">
            <LocalizedText translationKey="dweb_salesforce_case_status_monitor_email" />
          </Text.Body2>
          <Spacer $height="1.4rem" />
          {status == 'pending' && (
            <>
              <Text.Body2 $color="neutral-darker">
                <LocalizedText translationKey="dweb_salesforce_case_status_pending_reply_instruction" />
              </Text.Body2>
              <Spacer $height="1.4rem" />
            </>
          )}
          {status != 'closed' && (
            <Text.Caption>
              <LocalizedText translationKey="dweb_salesforce_case_status_no_additional_submissions" />
            </Text.Caption>
          )}
        </Container.Base>
      </Panel>
      <Spacer $height="2.4rem" />
      <PanelTitle>
        <LocalizedText translationKey="dweb_dashboard_salesforce_case_status_form_summary" />
      </PanelTitle>
      <Panel left>
        <Text.Caption>
          <LocalizedText translationKey="dweb_dashboard_salesforce_case_status_form_type" />
        </Text.Caption>
        <Spacer $height="0.8rem" />
        <Text.Body2 $color="neutral-black">{get(summary, 'subject', '-')}</Text.Body2>
        <Spacer $height="1.6rem" />
        <Text.Caption>
          <LocalizedText translationKey="dweb_dashboard_salesforce_case_status_date_submitted" />
        </Text.Caption>
        <Spacer $height="0.8rem" />
        <Text.Body2 $color="neutral-black">{get(summary, 'date_submitted', '-')}</Text.Body2>
        <Spacer $height="1.6rem" />
        {type === 'appeal' && (
          <>
            <Text.Caption>
              <LocalizedText translationKey="dweb_dashboard_salesforce_case_status_state_residency" />
            </Text.Caption>
            <Spacer $height="0.8rem" />
            <Text.Body2 $color="neutral-black">{get(summary, 'state', '-')}</Text.Body2>
            <Spacer $height="1.6rem" />
            <Text.Caption>
              <LocalizedText translationKey="dweb_dashboard_salesforce_case_status_support_id" />
            </Text.Caption>
            <Spacer $height="0.8rem" />
            <Text.Body2 $color="neutral-black">{get(summary, 'support_id', '-')}</Text.Body2>
            <Spacer $height="1.6rem" />
          </>
        )}
        <Text.Caption>{type === 'appeal' ? 'Description of Appeal' : 'Description'}</Text.Caption>
        <Spacer $height="0.8rem" />
        <Text.Body2 $color="neutral-black">
          {type === 'appeal' ? get(summary, 'rich_description', '-') : get(summary, 'description', '-')}
        </Text.Body2>
        <Spacer $height="1.6rem" />
      </Panel>
    </>
  )
}

export default CaseStatus
